<template>
  <div class="top-controls" :class="{
    'dark-theme': $vuetify.theme.dark,
    'light-theme': !$vuetify.theme.dark,
    'video-mode-active': isVideoMode
  }">
    <v-row class="justify-space-between pa-3 mt-2 align-start">
      <div class="topButtonsLeft ml-2 ml-md-6 pa-3 d-flex flex-column align-end" v-show="!isVideoFullscreen">
        <!-- HIGH-QUALITY AUDIO SWITCH -->
        <v-row
          v-if="!isVideoMode"
          class="d-flex align-center highQualityAudioSwitchContainer"
        >
          <span class="switchCaption">hi-fi: </span>
          <v-switch
            v-model="highQualityAudioMode"
            class="ml-2 topButtonsSwitch"
            hide-details
          ></v-switch>
        </v-row>

        <!-- CREATE PLAYLIST MODE SWITCH (only on master playlist) -->
        <v-row
          v-if="isLoggedIn && !isVideoMode && (!playlistID && !isPollResultsMode)"
          class="d-flex align-center createPlaylistSwitchContainer"
        >
          <span class="switchCaption">playlist: </span>
          <v-switch
            v-model="createPlaylistMode"
            class="ml-2 topButtonsSwitch"
            hide-details
          ></v-switch>
        </v-row>

        <!-- POLL MODE SWITCH (audio master playlist only) -->
        <v-row
          v-if="isLoggedIn && !isVideoMode && (!playlistID && !isPollResultsMode)"
          class="d-flex align-center pollSwitchContainer"
        >
          <span class="switchCaption">poll: </span>
          <v-switch
            v-model="pollMode"
            class="ml-2 topButtonsSwitch"
            hide-details
          ></v-switch>
        </v-row>
      </div>

      <div class="topButtonsRight" :class="{ 'logged-in': isLoggedIn }" v-show="!isVideoFullscreen">
        <!-- HOME BUTTON -->
        <v-tooltip
          v-if="isLoggedIn && playlistID"
          :disabled="$vuetify.breakpoint.smAndDown"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="goHome"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">mdi-home</v-icon>
            </v-btn>
          </template>
          <span>Home</span>
        </v-tooltip>

        <!-- SORT ALPHABETICALLY BUTTON -->
        <!-- Sorting is offered to everyone (even logged out, on any playlist)
             once the list has enough tracks — see ui/shouldShowSortOptions. -->
        <v-tooltip
          v-if="sortOrder === 'chronologically' && shouldShowSortOptions"
          :disabled="$vuetify.breakpoint.smAndDown"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="sortAlphabetically"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">
                mdi-sort-alphabetical-descending-variant
              </v-icon>
            </v-btn>
          </template>
          <span>Sort Alphabetically</span>
        </v-tooltip>

        <!-- SORT CHRONOLOGICALLY BUTTON -->
        <v-tooltip
          v-if="sortOrder === 'alphabetically' && shouldShowSortOptions"
          :disabled="$vuetify.breakpoint.smAndDown"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="sortChronologically"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">mdi-sort-calendar-ascending</v-icon>
            </v-btn>
          </template>
          <span>Sort Chronologically</span>
        </v-tooltip>

        <!-- SEARCH BUTTON -->
        <v-tooltip
          v-if="isLoggedIn && (!playlistID && !isPollResultsMode)"
          :disabled="$vuetify.breakpoint.smAndDown"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="toggleSearchBar"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>Search</span>
        </v-tooltip>

        <!-- VIDEO MODE BUTTON -->
        <v-tooltip
          v-if="isLoggedIn && (!playlistID && !isPollResultsMode)"
          :disabled="$vuetify.breakpoint.smAndDown"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="toggleVideoMode"
              :color="isVideoMode ? 'primary' : ''"
              :class="['mr-2', 'my-1', 'deactivateButtonBugFix', { 'active-button': isVideoMode }]"
            >
              <v-icon v-on="on">mdi-video</v-icon>
            </v-btn>
          </template>
          <span>{{ isVideoMode ? 'Switch to Audio' : 'Switch to Video' }}</span>
        </v-tooltip>

        <!-- DARK MODE BUTTON -->
        <v-tooltip
          v-if="!$vuetify.theme.dark"
          :disabled="$vuetify.breakpoint.smAndDown"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="toggleDarkMode"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on" class="mr-1">mdi-moon-waxing-crescent</v-icon>
            </v-btn>
          </template>
          <span>Dark Mode</span>
        </v-tooltip>

        <!-- LIGHT MODE BUTTON -->
        <v-tooltip 
          v-else 
          :disabled="$vuetify.breakpoint.smAndDown" 
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="toggleDarkMode"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">mdi-white-balance-sunny</v-icon>
            </v-btn>
          </template>
          <span>Light Mode</span>
        </v-tooltip>

        <!-- UPLOAD BUTTON -->
        <v-tooltip v-if="isLoggedIn && isSuperAdmin" bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="showUploadDialog"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Add audio or video</span>
        </v-tooltip>

        <!-- LOG OUT BUTTON -->
        <v-tooltip
          v-if="isLoggedIn"
          :disabled="$vuetify.breakpoint.smAndDown"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="logout"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">mdi-logout</v-icon>
            </v-btn>
          </template>
          <span>Log Out</span>
        </v-tooltip>

        <!-- LOG IN BUTTON -->
        <v-tooltip 
          v-else 
          :disabled="$vuetify.breakpoint.smAndDown" 
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :x-small="$vuetify.breakpoint.xs"
              :small="$vuetify.breakpoint.sm"
              fab
              @click="showLoginDialog"
              class="mr-2 my-1 deactivateButtonBugFix"
            >
              <v-icon v-on="on">mdi-lock-outline</v-icon>
            </v-btn>
          </template>
          <span>Log In</span>
        </v-tooltip>
      </div>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VideoService from '@/services/VideoService';

export default {
  name: 'TopControls',

  data() {
    return {
      isVideoFullscreen: false,
    };
  },

  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isLoggedIn',
      isSuperAdmin: 'auth/isSuperAdmin',
      playlistID: 'playlist/playlistID',
      sortOrder: 'ui/sortOrder',
      isShowingSearchBar: 'ui/isShowingSearchBar',
      isVideoMode: 'ui/isVideoMode',
      isPollResultsMode: 'ui/isPollResultsMode',
      shouldShowSortOptions: 'ui/shouldShowSortOptions',
    }),

    highQualityAudioMode: {
      get() {
        return this.$store.getters['ui/isHighQualityAudioMode'];
      },
      set(value) {
        this.$store.dispatch('ui/setHighQualityAudioMode', value);
      }
    },
    
    createPlaylistMode: {
      get() {
        return this.$store.getters['ui/isCreatePlaylistMode'];
      },
      set(value) {
        this.$store.dispatch('ui/setCreatePlaylistMode', value);
      }
    },

    pollMode: {
      get() {
        return this.$store.getters['ui/isPollMode'];
      },
      set(value) {
        this.$store.dispatch('ui/setPollMode', value);
        // When switching on, load the active poll; this flips back off with a
        // snackbar if there's no active poll or the user has already voted.
        if (value) {
          this.$store.dispatch('poll/enterPollMode');
        }
      }
    }
  },

  mounted() {
    // Set up fullscreen change listener
    VideoService.setFullscreenChangeCallback((isFullscreen) => {
      this.isVideoFullscreen = isFullscreen;
    });
  },

  beforeDestroy() {
    // Clean up listener
    VideoService.setFullscreenChangeCallback(null);
  },

  methods: {
    ...mapActions({
      logout: 'auth/logout',
      setHighQualityAudioMode: 'ui/setHighQualityAudioMode',
      setCreatePlaylistMode: 'ui/setCreatePlaylistMode',
      setSortOrder: 'ui/setSortOrder',
      toggleSearchBarAction: 'ui/toggleSearchBar',
      showSnackbar: 'ui/showSnackbar',
    }),
    
    goHome() {
      if (this.isLoggedIn) {
        this.$store.dispatch('playlist/loadMasterPlaylist');
        this.$router.push('/');
      }
    },
    
    sortAlphabetically() {
      this.setSortOrder('alphabetically');
    },
    
    sortChronologically() {
      this.setSortOrder('chronologically');
    },
    
    toggleSearchBar() {
      this.toggleSearchBarAction();
    },
    
    toggleDarkMode() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
    },

    toggleVideoMode() {
      this.$store.dispatch('ui/toggleVideoMode');
    },

    showUploadDialog() {
      this.$emit('showUpload');
    },

    showLoginDialog() {
      this.$emit('showLogin');
    }
  }
};
</script>

<style scoped>
.top-controls {
  width: 100%;
  z-index: 10000;
  text-align: right;
  pointer-events: auto;
  padding-top: 10px;
}

.dark-theme .switchCaption {
  color: white;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7);
}

.light-theme .switchCaption {
  color: black;
  text-shadow: 1px 1px 3px rgba(255, 255, 255, 0.7);
}

.topButtonsSwitch {
  margin-top: -5px !important;
}

.deactivateButtonBugFix:focus::before {
  opacity: 0 !important;
}

/* Active button styling for better contrast */
.dark-theme .active-button {
  background-color: #e0e0e0 !important; /* Light gray background in dark mode */
}

.dark-theme .active-button .v-icon {
  color: #212121 !important; /* Dark gray/almost black icon in dark mode */
}

.light-theme .active-button {
  background-color: #424242 !important; /* Dark gray background in light mode */
}

.light-theme .active-button .v-icon {
  color: #e0e0e0 !important; /* Light gray icon in light mode */
}

.highQualityAudioSwitchContainer,
.createPlaylistSwitchContainer,
.pollSwitchContainer {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.highQualityAudioSwitchContainer {
  margin-top: 5px !important;
}

.createPlaylistSwitchContainer,
.pollSwitchContainer {
  margin-top: 23px !important;
}

.switchCaption {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0.0333333333em !important;
  font-family: "Roboto", sans-serif !important;
}


@media only screen and (max-width: 715px) {
  .topButtonsRight.logged-in {
    display: flex;
    flex-wrap: wrap;
    max-width: 100px;
    justify-content: flex-start;
  }

  .topButtonsLeft {
    max-height: 60px;
  }
}

@media only screen and (max-width: 650px) {
  .createPlaylistSwitchContainer,
  .pollSwitchContainer {
    margin-top: 15px !important;
  }
  .createPlaylistSwitchContainer,
  .highQualityAudioSwitchContainer,
  .pollSwitchContainer {
    max-height: 25px;
  }
}

@media only screen and (max-width: 600px) {
  .topButtonsRight {
    margin-top: -11px;
    margin-right: -6px;
  }
  .highQualityAudioSwitchContainer {
    margin-top: -6px !important;
  }
}

@media only screen and (max-width: 450px) {
  .switchCaption {
    font-size: 0.57rem !important;
  }

  .v-btn--fab.v-size--x-small {
    height: 29px !important;
    width: 29px !important;
  }
}

</style>